import {
    GridRow,
    GridCell,
    TextButton,
    Breadcrumb,
    List,
    ListItem,
    Tab,
    TabBar,
    Typography,
    TextField,
    ExposedDropdownMenu,
    OverflowMenu,
} from "@episerver/ui-framework";
import React, { FunctionComponent, Fragment, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useHistory, useParams } from "react-router-dom";
import AddressList from "../../AddressManager/components/AddressList";
import { Address, InView } from "../../AddressManager/model/address";
import { EnumData } from "../../ContactManager/model/contact";
import { fetchEnumsByType } from "../../EnumManager/reducers/asyncThunks";
import { enumFormTitle, initialEnumFormState, onOpenDialog } from "../../EnumManager/reducers/enumFormReducer";
import { onChangeTotalCount } from "../reducers/childOrganizationListReducer";
import { enumType } from "../../EnumManager/reducers/enumManagerReducer";
import { Organization, OrganizationTab } from "../model/organization";
import { fetchOrganizationById, updateOrganization } from "../reducers/asyncThunks";
import {
    changeTitle,
    setParentId,
    organizationCustomerGroupsSelector,
    titleSelector,
    organizationSelector,
    businessCategoriesSelector,
    organizationTypesSelector,
    organizationTitle,
    breadcrumbItemsSelector,
    updateBreadcrumb,
    activeTabSelector,
    changeActiveTab,
    updateOrganizationId,
    onChangeProperty,
    errorSelector,
    onAddOrUpdateChildren,
    reset as resetData,
} from "../reducers/organizationDetailReducer";
import AddChildOrganizations from "./AddChildOrganizations";
import AddContactsToOrganization from "./AddContactsToOrganization";
import AddParentOrganization from "./AddParentOrganization";
import ChildOrganizationList from "./ChildOrganizationList";
import ContactListInOrganization from "./ContactListInOrganization";
import { useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { Snackbar, SnackbarAction } from "@rmwc/snackbar";
import { errorSelector as addChildErrorSeletor, clearAddChildError } from "../reducers/addChildOrganizationsReducer";
import { Controller, useForm } from "react-hook-form";
import ExtendedProperty from "../../ExtendedProperty/ExtendedProperty";
import { Property } from "../../../businessFoundation/MetaClassData/MetaClassDataList/MetaClassDataModel";

export const limitString = (val: string, num: number) => {
    if (val && val.length > num) {
        return val.substring(0, num) + "...";
    } else {
        return val;
    }
};

const OrganizationDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { organizationId } = useParams<{ organizationId: string }>();
    const title = useAppSelector(titleSelector);
    const initialOrganization = useAppSelector(organizationSelector);
    const organizationTypes = useAppSelector(organizationTypesSelector);
    const organizationCustomerGroups = useAppSelector(organizationCustomerGroupsSelector);
    const businessCategories = useAppSelector(businessCategoriesSelector);
    const breadcrumbItems = useAppSelector(breadcrumbItemsSelector);
    const activeTab = useAppSelector(activeTabSelector);
    const [errorOpen, setErrorOpen] = useState(false);
    const error = useAppSelector(errorSelector);
    const addChildError = useAppSelector(addChildErrorSeletor);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        setError,
        clearErrors,
        trigger,
        reset,
        formState: { errors },  
    } = useForm<Organization>({
        mode: "onChange",
        defaultValues: initialOrganization,
    });

    const values = watch();

    useEffect(() => {
        reset(initialOrganization);
    }, [initialOrganization]);

    const initComponent = async () => {
        dispatch(changeActiveTab(0));
        dispatch(fetchEnumsByType(enumType.OrganizationType));
        dispatch(fetchEnumsByType(enumType.ContactGroup));
        dispatch(fetchEnumsByType(enumType.BusinessCategory));
        dispatch(updateOrganizationId(organizationId));
        await dispatch(fetchOrganizationById(organizationId));
        if (organizationId) {
            dispatch(changeActiveTab(0));
            dispatch(changeTitle(organizationTitle.Edit));
        } else {
            dispatch(changeTitle(organizationTitle.Add));
        }
    };

    const onSubmit = async (organization: Organization) => {
        await dispatch(
            updateOrganization({
                ...organization,
                primaryKeyId: organizationId ? organization.primaryKeyId : null,
                businessCategoryId: organization.businessCategoryId === "0" ? null : organization.businessCategoryId,
                organizationCustomerGroupId:
                    organization.organizationCustomerGroupId === "0" ? null : organization.organizationCustomerGroupId,
                organizationTypeId: organization.organizationTypeId === "0" ? null : organization.organizationTypeId,
                parentId: organization.parentId === "" ? null : organization.parentId,
                extendedProperties:
                    organization.extendedProperties?.map((prop: Property) => ({
                        name: prop.name,
                        value: prop.value,
                    })) ?? [],
                addresses: organization.addresses?.map((address: Address) => {
                    return {
                        ...address,
                        extendedProperties:
                            address.extendedProperties?.map((prop: Property) => ({
                                name: prop.name,
                                value: prop.value,
                            })) ?? [],
                    };
                }),
                children: organization.children.map(c => c.primaryKeyId)
            })
        )
            .then(unwrapResult)
            .then(() => {
                history.push("/Organizations");
            })
            .catch(() => {
                setErrorOpen(true);
            });
    };

    useEffect(() => {
        initComponent();
    }, [organizationId]);

    useEffect(() => {
        dispatch(
            updateBreadcrumb({
                level: 2,
                active: true,
                title: initialOrganization.name,
                link: "",
            })
        );
    }, [initialOrganization.name]);

    useEffect(() => {
        return () => {
            dispatch(onChangeProperty(values));
            dispatch(resetData());
        };
    }, []);

    useEffect(() => {
        dispatch(onChangeProperty(values));
    }, [activeTab != OrganizationTab.Details]);

    const renderDetails = (): React.ReactNode => {
        return (
            <form
                autoComplete="off"
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <GridRow className="grid-row">
                    <GridCell span={6}>
                        <div className="control">
                            <Controller
                                name="name"
                                control={control}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "The name is required"
                                    },
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        id="name"
                                        autoFocus
                                        maxLength={100}
                                        outlined
                                        label="Name"
                                        name="name"
                                        helperText={errors.name?.message ?? ""}
                                        invalid={!!errors.name?.message}
                                        value={value ?? ""}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </div>
                    </GridCell>   
                    <GridCell span={6}>
                        <div className="control">
                            <Controller
                                name="description"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <TextField
                                        id="description"
                                        autoFocus
                                        maxLength={100}
                                        outlined
                                        label="Description"
                                        name="description"
                                        helperText={errors.description?.message ?? ""}
                                        invalid={!!errors.description?.message}
                                        value={value ?? ""}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </div>
                    </GridCell>         
                    <Fragment>
                        {values.extendedProperties?.map((property: Property, index: number) => {
                            return (
                                <GridCell span={6} key={index}>
                                    <div className="control">
                                        <ExtendedProperty
                                            index={index}
                                            className="Organization"
                                            property={property}
                                            clearErrors={clearErrors}
                                            control={control}
                                            errors={errors}
                                            setError={setError}
                                            setValue={setValue}
                                            trigger={trigger}
                                            key={index}
                                        />
                                    </div>
                                </GridCell>
                            );
                        })}
                    </Fragment>
                </GridRow>
            </form>
        );
    };

    const renderAddresses = (): React.ReactNode => {
        return <AddressList inTab inView={InView.Organization} viewId={organizationId ?? ""} />;
    };

    const renderContacts = (): React.ReactNode => {
        return (
            <Fragment>
                <AddContactsToOrganization organizationId={organizationId ?? ""} />
                <ContactListInOrganization organizationId={organizationId ?? ""} />
            </Fragment>
        );
    };

    const renderOrganizations = (): React.ReactNode => {
        return (
            <Fragment>
                <AddChildOrganizations addChildren={(children: Organization[]) => {
                    if (children?.length ?? 0 > 0) {
                        dispatch(onAddOrUpdateChildren({
                            parentOrganizationId: organizationId,
                            children: children,
                        }));
                        dispatch(onChangeTotalCount(initialOrganization.children.length + children.length));
                    }
                }} />
                <ChildOrganizationList parentOrganizationId={organizationId ?? ""} />
                {errorOpen && <Snackbar
                    open={addChildError != null}
                    message={
                        <Typography tag="p" use={"body1"}>
                            {addChildError}
                        </Typography>
                    }
                    timeout={-1}
                    dismissesOnAction
                    onClose={() => {
                        dispatch(clearAddChildError());
                    }}
                    action={[<SnackbarAction label="Close" />]}
                ></Snackbar>}
            </Fragment>
        );
    };

    return (
        <Fragment>
            {!organizationId && (
                <Fragment>
                    <Typography use="headline1">{title}</Typography>
                    <br />
                </Fragment>
            )}
            {organizationId && (
                <React.Fragment>
                    <Breadcrumb items={breadcrumbItems} />
                    <br></br>
                    <TabBar
                        activeTabIndex={activeTab}
                        onActivate={(e) => {
                            dispatch(changeActiveTab(e.detail.index));
                        }}
                    >
                        <Tab>Details</Tab>
                        <Tab>Contacts</Tab>
                    </TabBar>
                </React.Fragment>
            )}
            <br />
            {<div className={activeTab === OrganizationTab.Details ? "" : "hidden"}> 
               {renderDetails()}
            </div>}
            {organizationId && activeTab === OrganizationTab.Contacts && renderContacts()}
            <br />
            <div className="edit-form-buttons right-buttons">
                <TextButton onClick={() => history.push("/Organizations")}>Cancel</TextButton>
                <TextButton contained onClick={() => handleSubmit(onSubmit)()}>
                    Save
                </TextButton>
            </div>
            {errorOpen && <Snackbar
                open={errorOpen}
                message={
                    <Typography tag="p" use={"body1"}>
                        {error}
                    </Typography>
                }
                timeout={-1}
                dismissesOnAction
                onClose={() => {
                    setErrorOpen(false);
                }}
                action={[<SnackbarAction label="Close" />]}
            ></Snackbar>}
        </Fragment>
    );
};

export default OrganizationDetail;
